import {
  Box,
  CircularProgress,
  Container,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GET_REVENUE_CHART_DATA } from 'src/apiService/apiDeclaration';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { t } from 'i18next';
import LineChart from 'src/components/dashboardComponents/LineChart';
import { formatDate, formatMonthReponse, formatNumberSpacing, formatWeekResponse } from 'src/constants';
dayjs.extend(quarterOfYear);
//console
const RevenuePage = () => {
  const location = useLocation();
  const mode = location.state || 'DAILY';
  const [key, setKey] = useState('');

  const [fromDate, setFromDate] = useState(() => {
    switch (mode) {
      case 'WEEKLY':
        setKey('week');
        return dayjs().subtract(4, 'week');
      case 'MONTHLY':
        setKey('month');
        return dayjs().subtract(12, 'month');
      case 'QUARTERLY':
        setKey('quarter');
        return dayjs().subtract(3, 'quarter');
      case 'YEARLY':
        setKey('year');
        return dayjs().subtract(4, 'year');
      default:
        setKey('date');
        return dayjs().subtract(6, 'day');
    }
  });
  const [toDate, setToDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
  };
  const handleToDateChange = (newValue) => {
    setToDate(newValue);
  };

  const getData = async () => {
    setLoading(true);
    try {
      let resp = await GET_REVENUE_CHART_DATA(mode, toDate, fromDate);
      setData(resp.data);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkValid = (fromDate, toDate, mode) => {
    const diffInUnits = (unit) => dayjs(toDate).diff(dayjs(fromDate), unit);

    switch (mode) {
      case 'WEEKLY':
        return diffInUnits('week') <= 26; // 26 weeks
      case 'MONTHLY':
        return diffInUnits('month') <= 24; // 24 months
      case 'QUARTERLY':
        return diffInUnits('quarter') <= 12; // 12 quarters
      case 'YEARLY':
        return diffInUnits('year') <= 12; // 12 years
      default: // DAILY
        return diffInUnits('day') <= 30; // 30 days
    }
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: ' #414141',
      color: theme.palette.common.white,
      fontSize: 18,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 16,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', height: '50vh', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      );
    } else if (!loading && !checkValid(fromDate, toDate, mode)) {
      return (
        <Box
          sx={{
            display: 'flex',
            height: '50vh',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 200 }}>
            {t('taglines.dateError')}
          </Typography>
        </Box>
      );
    } else {
      return (
        <LineChart
          data={data}
          label={`${t('revenue.revenue')} ${t(`revenue.${mode}`)}`}
          yLabel={t('revenue.revenueLabel')}
          labelKey={key}
        />
      );
    }
  };

  useEffect(() => {
    if (checkValid(fromDate, toDate, mode) && fromDate && toDate) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { md: 'row', xs: 'column' },
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h3" sx={{ color: '#acd629' }}>
            {t('revenue.revenue')} {t(`revenue.${mode}`)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('revenue.startDate')}
              value={fromDate}
              onChange={handleFromDateChange}
              format="DD/MM/YYYY"
              renderInput={(params) => <input {...params} />}
            />
          </LocalizationProvider>
          <Box> to </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('revenue.endDate')}
              value={toDate}
              onChange={handleToDateChange}
              format="DD/MM/YYYY"
              renderInput={(params) => <input {...params} />}
              maxDate={dayjs()}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', maxHeight: '50vh', overflowY: 'auto' }}>
        {checkValid(fromDate, toDate, mode) && (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: '600px' }} aria-label="simple table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell align="center">{t(`revenue.${key}`)}</StyledTableCell>
                  <StyledTableCell align="center">{t(`revenue.revenue`)}</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {data?.map((row, index) => (
                  <StyledTableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <StyledTableCell align="center" component="th" scope="row" sx={{ fontsize: '2rem', width: '50%' }}>
                      {mode === 'DAILY'
                        ? formatDate(row?.[key])
                        : mode === 'WEEKLY'
                          ? formatWeekResponse(row?.[key])
                          : mode === 'MONTHLY'
                            ? formatMonthReponse(row?.[key])
                            : row?.[key]}
                    </StyledTableCell>
                    <StyledTableCell align="center">${formatNumberSpacing(row.revenue)}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {renderContent()}
      </Box>
    </Container>
  );
};

export default RevenuePage;
